import React, { useState, useEffect } from "react";
import { getOperationHistory, logOperation } from "../services/firestore";
import { OperationHistory } from "../types/artwork";
import { formatDate } from "../utils";
import "./OperationHistory.css";

interface OperationHistoryProps {
  artworkId: string;
  defaultShowForm?: boolean;
}

const OperationHistoryComponent: React.FC<OperationHistoryProps> = ({
  artworkId,
  defaultShowForm = false, // Default chiuso
}) => {
  const [operations, setOperations] = useState<OperationHistory[]>([]);
  const [newOperation, setNewOperation] = useState<OperationHistory>({
    date: new Date(),
    type: "call",
    description: "",
    outcome: "",
    nextAction: "",
    nextActionStatus: undefined, // ✅ NUOVO CAMPO
    createdAt: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(defaultShowForm); // Usa la prop per il valore iniziale
  const [filterType, setFilterType] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const fetchOperations = async () => {
      try {
        setLoading(true);
        const operationHistory = await getOperationHistory(artworkId);
        setOperations(operationHistory);
      } catch (error) {
        console.error("Error fetching operation history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOperations();
  }, [artworkId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);

      // 🔥 Costruisci l'oggetto senza undefined
      const opToSave: any = {
        ...newOperation,
      };
      if (opToSave.nextActionStatus === undefined) {
        delete opToSave.nextActionStatus;
      }

      await logOperation(artworkId, opToSave);
      const updatedOperations = await getOperationHistory(artworkId);
      setOperations(updatedOperations);
      setNewOperation({
        date: new Date(),
        type: "call",
        description: "",
        outcome: "",
        nextAction: "",
        nextActionStatus: undefined,
        createdAt: new Date(),
      });
      setShowForm(false);
    } catch (error) {
      console.error("Error adding operation:", error);
    } finally {
      setSaving(false);
    }
  };

  // Filter and sort operations
  const filteredOperations = operations
    .filter((op) => filterType === "all" || op.type === filterType)
    .sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

  // Get operation statistics
  const stats = [
    {
      type: "all",
      label: "Total",
      count: operations.length,
      icon: "📊",
      color: "#007bff",
    },
    {
      type: "call",
      label: "Calls",
      count: operations.filter((op) => op.type === "call").length,
      icon: "📞",
      color: "#007bff",
    },
    {
      type: "email",
      label: "Emails",
      count: operations.filter((op) => op.type === "email").length,
      icon: "✉️",
      color: "#17a2b8",
    },
    {
      type: "visit",
      label: "Visits",
      count: operations.filter((op) => op.type === "visit").length,
      icon: "🏛️",
      color: "#28a745",
    },
    {
      type: "status_change",
      label: "Changes",
      count: operations.filter((op) => op.type === "status_change").length,
      icon: "🔄",
      color: "#ffc107",
    },
    {
      type: "note_added",
      label: "Notes",
      count: operations.filter((op) => op.type === "note_added").length,
      icon: "📝",
      color: "#6f42c1",
    },
  ].filter((stat) => stat.type === "all" || stat.count > 0);

  return (
    <div className="operation-history-container">
      {/* Header */}
      <div className="operation-history-header">
        <div className="operation-history-header-content">
          <h3>
            <span className="operation-history-header-icon">📋</span>
            Operation History
          </h3>
          <p>Track all interactions and manage this artwork</p>
        </div>

        <button
          onClick={() => setShowForm(!showForm)}
          className={`operation-history-toggle-button ${
            showForm ? "show-form" : "hide-form"
          }`}
        >
          {showForm ? <>🙈 Hide Form</> : <>➕ Add Operation</>}
        </button>
      </div>

      {/* Add Operation Form */}
      {showForm && (
        <div className="operation-history-form-container">
          <h4 className="operation-history-form-title">
            <span className="operation-history-form-title-icon">✨</span>
            Add New Operation
          </h4>

          <form onSubmit={handleSubmit}>
            <div className="operation-history-form-grid">
              {/* Date Input */}
              <div>
                <label className="operation-history-form-label">
                  📅 Date & Time:
                </label>
                <input
                  type="datetime-local"
                  className="operation-history-form-input"
                  value={newOperation.date.toISOString().slice(0, 16)}
                  onChange={(e) =>
                    setNewOperation({
                      ...newOperation,
                      date: new Date(e.target.value),
                    })
                  }
                />
              </div>

              {/* Type Select */}
              <div>
                <label className="operation-history-form-label">
                  🏷️ Operation Type:
                </label>
                <select
                  className="operation-history-form-select"
                  value={newOperation.type}
                  onChange={(e) =>
                    setNewOperation({
                      ...newOperation,
                      type: e.target.value as any,
                    })
                  }
                >
                  <option value="call">📞 Phone Call</option>
                  <option value="email">✉️ Email</option>
                  <option value="visit">🏛️ Site Visit</option>
                  <option value="status_change">🔄 Status Change</option>
                  <option value="note_added">📝 Note Added</option>
                  <option value="other">🔧 Other</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div className="operation-history-form-field">
              <label className="operation-history-form-label">
                📄 Description:{" "}
                <span className="operation-history-form-required">*</span>
              </label>
              <textarea
                className="operation-history-form-textarea"
                value={newOperation.description}
                onChange={(e) =>
                  setNewOperation({
                    ...newOperation,
                    description: e.target.value,
                  })
                }
                required
                rows={3}
                placeholder="Describe what happened during this operation..."
              />
            </div>

            <div className="operation-history-form-grid">
              {/* Outcome */}
              <div>
                <label className="operation-history-form-label">
                  ✅ Outcome:
                </label>
                <input
                  type="text"
                  className="operation-history-form-input"
                  value={newOperation.outcome || ""}
                  onChange={(e) =>
                    setNewOperation({
                      ...newOperation,
                      outcome: e.target.value,
                    })
                  }
                  placeholder="What was the result?"
                />
              </div>

              {/* Next Action */}
              <div>
                <label className="operation-history-form-label">
                  ⏭️ Next Action:
                </label>
                <input
                  type="text"
                  className="operation-history-form-input"
                  value={newOperation.nextAction || ""}
                  onChange={(e) =>
                    setNewOperation({
                      ...newOperation,
                      nextAction: e.target.value,
                    })
                  }
                  placeholder="What should be done next?"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="operation-history-form-actions">
              <button
                type="button"
                className="operation-history-form-button operation-history-form-button-cancel"
                onClick={() => setShowForm(false)}
              >
                ❌ Cancel
              </button>
              <button
                type="submit"
                className="operation-history-form-button operation-history-form-button-submit"
                disabled={saving || !newOperation.description.trim()}
              >
                {saving ? (
                  <>
                    <div className="operation-history-form-spinner"></div>
                    Saving...
                  </>
                ) : (
                  <>💾 Save Operation</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Quick Stats */}
      {stats.length > 1 && (
        <div className="operation-history-stats">
          {stats.map((stat) => (
            <div
              key={stat.type}
              onClick={() => setFilterType(stat.type)}
              className={`operation-history-stat-card ${
                filterType === stat.type ? "active" : "inactive"
              }`}
              style={{
                borderColor:
                  filterType === stat.type ? stat.color : "transparent",
                boxShadow:
                  filterType === stat.type
                    ? `0 4px 15px ${stat.color}33`
                    : "0 2px 8px rgba(0, 0, 0, 0.06)",
              }}
            >
              <div className="operation-history-stat-icon">{stat.icon}</div>
              <div
                className="operation-history-stat-count"
                style={{ color: stat.color }}
              >
                {stat.count}
              </div>
              <div className="operation-history-stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      {operations.length > 0 && (
        <div className="operation-history-filters">
          <div className="operation-history-filters-content">
            <div className="operation-history-filters-left">
              <div className="operation-history-filters-sort">
                <label>📅 Sort:</label>
                <select
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(e.target.value as "asc" | "desc")
                  }
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>

            <div className="operation-history-filters-right">
              <span>
                📊 Showing {filteredOperations.length} of {operations.length}{" "}
                operations
              </span>
              {filterType !== "all" && (
                <button
                  onClick={() => setFilterType("all")}
                  className="operation-history-filters-clear"
                >
                  🧹 Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Operations List */}
      <div className="operation-history-list">
        {loading && operations.length === 0 && (
          <div className="operation-history-loading">
            <div className="operation-history-loading-spinner"></div>
            <p className="operation-history-loading-text">
              Loading operations...
            </p>
          </div>
        )}

        {operations.length === 0 && !loading && (
          <div className="operation-history-empty">
            <div className="operation-history-empty-icon">📝</div>
            <h4 className="operation-history-empty-title">No Operations Yet</h4>
            <p className="operation-history-empty-text">
              Start tracking your artwork interactions by adding your first
              operation.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="operation-history-empty-button"
            >
              ➕ Add First Operation
            </button>
          </div>
        )}

        {filteredOperations.length === 0 &&
          operations.length > 0 &&
          !loading && (
            <div className="operation-history-no-results">
              <div className="operation-history-no-results-icon">🔍</div>
              <h4 className="operation-history-no-results-title">
                No Operations Found
              </h4>
              <p className="operation-history-no-results-text">
                No operations found for the selected filter.
              </p>
              <button
                onClick={() => setFilterType("all")}
                className="operation-history-no-results-button"
              >
                📊 Show All Operations
              </button>
            </div>
          )}

        {filteredOperations.length > 0 && (
          <div className="operation-history-cards">
            {filteredOperations.map((operation, index) => (
              <div key={operation.id || index} className="operation-card">
                {/* Decorative corner */}
                <div
                  className="operation-card-corner"
                  style={{
                    background: `linear-gradient(135deg, ${getOperationTypeColor(
                      operation.type
                    )}, ${getOperationTypeColor(operation.type)}aa)`,
                  }}
                ></div>

                {/* Header */}
                <div className="operation-card-header">
                  <div className="operation-card-header-left">
                    <span className="operation-card-icon">
                      {getOperationTypeIcon(operation.type)}
                    </span>
                    <div className="operation-card-info">
                      <div className="operation-card-date">
                        {formatDate(new Date(operation.date))}
                      </div>
                      <span
                        className="operation-card-type"
                        style={{
                          backgroundColor: getOperationTypeColor(
                            operation.type
                          ),
                        }}
                      >
                        {operation.type.replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="operation-card-section">
                  <div className="operation-card-section-header">
                    <span className="operation-card-section-icon">📄</span>
                    <strong className="operation-card-section-title">
                      Description:
                    </strong>
                  </div>
                  <div className="operation-card-description">
                    <p>{operation.description}</p>
                  </div>
                </div>

                {/* Outcome */}
                {operation.outcome && (
                  <div className="operation-card-section">
                    <div className="operation-card-section-header">
                      <span className="operation-card-section-icon">✅</span>
                      <strong className="operation-card-section-title">
                        Outcome:
                      </strong>
                    </div>
                    <div className="operation-card-outcome">
                      <p>{operation.outcome}</p>
                    </div>
                  </div>
                )}

                {/* Next Action */}
                {operation.nextAction && (
                  <div className="operation-card-next-action">
                    <div className="operation-card-next-action-header">
                      <span className="operation-card-next-action-icon">
                        ⏭️
                      </span>
                      <strong className="operation-card-next-action-title">
                        Next Action:
                      </strong>
                    </div>
                    <p>{operation.nextAction}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const getOperationTypeColor = (type: string) => {
  switch (type) {
    case "call":
      return "#007bff";
    case "email":
      return "#17a2b8";
    case "visit":
      return "#28a745";
    case "status_change":
      return "#ffc107";
    case "note_added":
      return "#6f42c1";
    default:
      return "#6c757d";
  }
};

const getOperationTypeIcon = (type: string) => {
  switch (type) {
    case "call":
      return "📞";
    case "email":
      return "✉️";
    case "visit":
      return "🏛️";
    case "status_change":
      return "🔄";
    case "note_added":
      return "📝";
    default:
      return "🔧";
  }
};

export default OperationHistoryComponent;
